import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { Inbox, Phone, Mail, MapPin, Clock, Check, X, Archive, Trash2, ArrowRight, MessageSquare, Sparkles, Building, Ruler } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';

const STATI = {
  nuovo: { label: 'Nuovo', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  in_lavorazione: { label: 'In lavorazione', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
  preventivo_inviato: { label: 'Preventivo inviato', color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  convertito: { label: 'Convertito', color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
  archiviato: { label: 'Archiviato', color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
};

const FONTI = {
  contact_form: 'Form contatti',
  quick_quote_widget: 'Widget preventivo rapido',
};

export default function LeadPage({ onNavigate }) {
  const [items, setItems] = useState([]);
  const [byStato, setByStato] = useState({});
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { stato: filter } : {};
      const { data } = await API.get('/api/lead', { params });
      setItems(data.items || []);
      setByStato(data.by_stato || {});
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const totale = Object.values(byStato).reduce((s, n) => s + n, 0);

  return (
    <div className="animate-fade-in" data-testid="lead-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <Inbox size={28} className="text-primary" /> Lead / Richieste Preventivo
          </h1>
          <p className="text-slate-500 mt-1">Tutte le richieste arrivate dal sito vetrina edilcima.it</p>
        </div>
        <button onClick={load} className="px-3 py-2 text-sm font-bold border border-slate-200 rounded-md hover:bg-slate-50" data-testid="lead-refresh">
          Aggiorna
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="lead-filters">
        <FilterBtn id="" current={filter} setF={setFilter} label="Tutti" count={totale} />
        {Object.entries(STATI).map(([k, v]) => (
          <FilterBtn key={k} id={k} current={filter} setF={setFilter} label={v.label} count={byStato[k] || 0} dot={v.dot} />
        ))}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-slate-400 py-12 text-center">Caricamento...</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-12 text-center" data-testid="lead-empty">
          <Inbox size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="font-bold text-slate-600">Nessun lead {filter ? `nello stato "${STATI[filter]?.label}"` : 'al momento'}</p>
          <p className="text-sm text-slate-400 mt-1">Le richieste dal sito appariranno automaticamente qui.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map(lead => <LeadCard key={lead.id} lead={lead} onOpen={() => setSelected(lead)} />)}
        </div>
      )}

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} onReload={() => { setSelected(null); load(); }} onNavigate={onNavigate} />}
    </div>
  );
}

function FilterBtn({ id, current, setF, label, count, dot }) {
  const active = current === id;
  return (
    <button onClick={() => setF(id)} data-testid={`lead-filter-${id || 'all'}`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-bold rounded-md transition-all border ${active ? 'bg-primary text-white border-primary' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
      {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />}
      {label}
      <span className={`text-xs px-1.5 py-0.5 rounded ${active ? 'bg-white/20' : 'bg-slate-100'}`}>{count}</span>
    </button>
  );
}

function LeadCard({ lead, onOpen }) {
  const stato = STATI[lead.stato] || STATI.nuovo;
  const dataObj = new Date(lead.created_at);
  return (
    <button onClick={onOpen} data-testid={`lead-${lead.id}`}
      className="text-left bg-white border border-slate-200 rounded-md p-4 hover:shadow-md hover:border-slate-300 transition-all">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-bold text-base truncate">{lead.nome}</h3>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${stato.color}`}>{stato.label}</span>
            {lead.fonte === 'quick_quote_widget' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-primary text-primary border border-primary">
                <Sparkles size={10} /> Widget
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            {lead.servizio && <span className="font-semibold text-slate-700">{lead.servizio}</span>}
            {lead.mq && <span className="inline-flex items-center gap-1"><Ruler size={10} /> {lead.mq}</span>}
            {lead.urgenza && <span className="inline-flex items-center gap-1"><Clock size={10} /> {lead.urgenza}</span>}
            {lead.localita && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {lead.localita}</span>}
          </div>
          <p className="text-sm text-slate-600 mt-2 line-clamp-2">{lead.messaggio}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Ricevuto</p>
          <p className="text-sm font-semibold">{dataObj.toLocaleDateString('it-IT')}</p>
          <p className="text-xs text-slate-400">{dataObj.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      </div>
    </button>
  );
}

function LeadDetail({ lead: initial, onClose, onReload, onNavigate }) {
  const confirm = useConfirm();
  const [lead, setLead] = useState(initial);
  const [note, setNote] = useState(initial.note_operatore || '');
  const [saving, setSaving] = useState('');
  const stato = STATI[lead.stato] || STATI.nuovo;

  const update = async (stato) => {
    setSaving(stato);
    try {
      const { data } = await API.patch(`/api/lead/${lead.id}`, { stato });
      setLead(data);
      onReload();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setSaving('');
  };

  const saveNote = async () => {
    setSaving('note');
    try {
      const { data } = await API.patch(`/api/lead/${lead.id}`, { note_operatore: note });
      setLead(data);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setSaving('');
  };

  const archivia = async () => {
    if (!await confirm({ title: 'Archivia lead', message: 'Vuoi archiviare questo lead?', confirmLabel: 'Archivia', variant: 'warning' })) return;
    setSaving('archivia');
    await API.post(`/api/lead/${lead.id}/archivia`);
    onReload();
  };

  const cancella = async () => {
    if (!await confirm({ title: 'Eliminazione definitiva', message: 'Stai per ELIMINARE definitivamente questo lead. Operazione non reversibile.', confirmLabel: 'Elimina', variant: 'danger' })) return;
    setSaving('cancella');
    await API.delete(`/api/lead/${lead.id}`);
    onReload();
  };

  const converti = async () => {
    if (!await confirm({ title: 'Converti in Cliente + Pratica', message: 'Verranno creati automaticamente un nuovo Cliente e una nuova Pratica collegata a questo lead.', confirmLabel: 'Crea Cliente + Pratica', variant: 'success' })) return;
    setSaving('converti');
    try {
      const { data } = await API.post(`/api/lead/${lead.id}/converti`, { crea_cliente: true, crea_pratica: true });
      alert(`Creato! Cliente: ${data.cliente_id?.slice(0, 8)}... · Pratica: ${data.pratica_id?.slice(0, 8)}...`);
      onReload();
      if (onNavigate) onNavigate('pratiche');
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setSaving('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[90vh] flex flex-col animate-fade-in overflow-hidden" data-testid="lead-detail">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-bold truncate">{lead.nome}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded text-xs font-bold border ${stato.color}`}>{stato.label}</span>
              <span className="text-xs text-slate-400">{new Date(lead.created_at).toLocaleString('it-IT')}</span>
              {lead.fonte && <span className="text-xs text-slate-400">· {FONTI[lead.fonte] || lead.fonte}</span>}
            </div>
          </div>
          <button onClick={onClose} aria-label="Chiudi"><X size={20} className="text-slate-400" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
          {/* Contatti */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {lead.email && <Info icon={Mail} label="Email" value={<a href={`mailto:${lead.email}`} className="text-primary hover:underline break-all">{lead.email}</a>} />}
            {lead.telefono && <Info icon={Phone} label="Telefono" value={<a href={`tel:${lead.telefono}`} className="text-primary hover:underline">{lead.telefono}</a>} />}
            {lead.servizio && <Info icon={Building} label="Servizio richiesto" value={lead.servizio} />}
            {lead.localita && <Info icon={MapPin} label="Località" value={lead.localita} />}
            {lead.mq && <Info icon={Ruler} label="Metratura" value={lead.mq} />}
            {lead.urgenza && <Info icon={Clock} label="Urgenza" value={lead.urgenza} />}
          </div>

          {/* Messaggio */}
          {lead.messaggio && (
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><MessageSquare size={11}/> Messaggio del cliente</p>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm whitespace-pre-wrap">{lead.messaggio}</div>
            </div>
          )}

          {/* Note operatore */}
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Note interne operatore</p>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} data-testid="lead-note"
              placeholder="Es: cliente ricontattato il 12/02, in attesa sopralluogo..."
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary" />
            <button onClick={saveNote} disabled={saving === 'note'} data-testid="lead-save-note"
              className="mt-2 px-3 py-1.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 rounded-md disabled:opacity-50">
              {saving === 'note' ? 'Salvataggio...' : 'Salva nota'}
            </button>
          </div>

          {/* Riferimenti */}
          {(lead.cliente_id || lead.pratica_id) && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm">
              <p className="font-bold text-green-800 mb-1">Lead già convertito</p>
              {lead.cliente_id && <p className="text-xs text-green-700">Cliente: <code className="bg-white px-1 rounded">{lead.cliente_id.slice(0, 8)}...</code></p>}
              {lead.pratica_id && <p className="text-xs text-green-700">Pratica: <code className="bg-white px-1 rounded">{lead.pratica_id.slice(0, 8)}...</code></p>}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 sm:p-5 border-t border-slate-200 bg-white sticky bottom-0 space-y-3">
          {/* Stato changer */}
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Cambia stato</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATI).filter(([k]) => k !== 'archiviato').map(([k, v]) => (
                <button key={k} onClick={() => update(k)} disabled={saving === k || lead.stato === k} data-testid={`lead-stato-${k}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md border transition-all ${lead.stato === k ? `${v.color} ring-2 ring-offset-1` : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-600'}`}>
                  <span className={`w-2 h-2 rounded-full ${v.dot}`} />
                  {saving === k ? '...' : v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {!lead.pratica_id && (
              <button onClick={converti} disabled={saving === 'converti'} data-testid="lead-converti"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-success text-white text-sm font-bold rounded-md hover:bg-green-700 disabled:opacity-50">
                <ArrowRight size={14} /> {saving === 'converti' ? 'Conversione...' : 'Crea Cliente + Pratica'}
              </button>
            )}
            {lead.pratica_id && (
              <button onClick={() => onNavigate && onNavigate('preventivi', { pratica_id: lead.pratica_id })} data-testid="lead-go-preventivi"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-bold rounded-md hover:bg-primary-hover">
                <Check size={14} /> Vai ai Preventivi pratica
              </button>
            )}
            <button onClick={archivia} disabled={saving === 'archivia'} data-testid="lead-archivia"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-sm font-bold rounded-md hover:bg-slate-50 disabled:opacity-50">
              <Archive size={14} /> Archivia
            </button>
            <button onClick={cancella} disabled={saving === 'cancella'} data-testid="lead-cancella"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 text-sm font-bold rounded-md hover:bg-red-50 disabled:opacity-50">
              <Trash2 size={14} /> Cancella
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon size={11} className="text-slate-400" />
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
}
